<div class="campaign-panel" [formGroup]="group">
  <div class="campaign-panel-head mb-3">
    <i class="campaign-panel-icon mdi mdi-message-badge-outline" aria-hidden="true"></i>
    <span>
      <b>{{ "rcsMessage.editor.composerTitle" | translate }}</b>
      <p>{{ "rcsMessage.editor.composerSubtitle" | translate }}</p>
    </span>
  </div>
  <div>
    <!-- ── Message format ──────────────────────────── -->
    <div class="d-flex justify-content-between align-items-center mt-3">
      <span class="campaign-group-label mb-0">
        {{ "rcsMessage.editor.formatGroupLabel" | translate }}
      </span>
      <span class="campaign-field-hint mt-0">
        {{ "rcsMessage.editor.formatGroupHint" | translate }}
      </span>
    </div>

    <div
      class="campaign-tiles mt-2 mb-3"
      role="radiogroup"
      [attr.aria-label]="'rcsMessage.editor.formatGroupLabel' | translate"
    >
      @for (tile of formatTiles; track tile.value) {
        <label class="campaign-tile" [class.is-selected]="isFormat(tile.value)">
          <input
            type="radio"
            [value]="tile.value"
            formControlName="type"
            class="campaign-pick-input"
            (change)="onFormatChange()"
          />
          <i class="campaign-tile-icon {{ tile.icon }}" aria-hidden="true"></i>
          <span class="campaign-tile-label">
            {{ tile.label ?? (tile.labelKey | translate) }}
          </span>
        </label>
      }
    </div>
    <field-error-component
      [field]="'type'"
      [label]="'rcsMessage.editor.formatGroupLabel' | translate"
      [formGroup]="group"
    />

    <!-- ── TEXT ──────────────────────────── -->
    @if (isFormat("text")) {
      <div class="row">
        <div class="col-lg-12 mb-3">
          <div class="d-flex justify-content-between align-items-center gap-2 mb-1">
            <label class="form-label mb-0">
              {{ "rcsMessage.editor.messageLabel" | translate }}
              <span class="text-danger">*</span>
            </label>
            <span class="campaign-field-hint mt-0 text-nowrap">
              {{ group.get("message")?.value?.length || 0 | number }}
              / {{ TEXT_LIMIT | number }}
            </span>
          </div>
          <!--
            No (paste) handler and no bold/italic: both exist to produce WhatsApp markup,
            which RCS delivers to the customer as literal characters.
          -->
          <textarea
            class="form-control"
            [placeholder]="'rcsMessage.editor.textPlaceholder' | translate"
            rows="8"
            [maxlength]="TEXT_LIMIT"
            formControlName="message"
          ></textarea>
          <field-error-component
            [field]="'message'"
            [label]="'rcsMessage.editor.messageLabel' | translate"
            [formGroup]="group"
          />
          <div
            class="campaign-editor-toolbar"
            role="toolbar"
            [attr.aria-label]="'rcsMessage.editor.toolbar' | translate"
          >
            <div class="position-relative">
              <button
                type="button"
                class="campaign-editor-btn"
                [disabled]="readonly"
                [attr.aria-label]="'rcsMessage.editor.emoji' | translate"
                (click)="toggleEmoji()"
              >
                <i class="mdi mdi-emoticon-outline" aria-hidden="true"></i>
              </button>

              @if (showEmoji) {
                <div
                  #emojiContainer
                  class="position-absolute start-0 mt-2 z-3"
                  (click)="$event.stopPropagation()"
                >
                  <emoji-mart
                    (emojiSelect)="addEmoji($event)"
                    [i18n]="translator"
                    [darkMode]="false"
                  >
                  </emoji-mart>
                </div>
              }
            </div>

            @if (variables.length) {
              <button
                type="button"
                class="campaign-editor-btn"
                [disabled]="readonly"
                [attr.aria-label]="'rcsMessage.editor.insertVariable' | translate"
                [matMenuTriggerFor]="variableMenu"
                [matMenuTriggerData]="{ control: group.get('message') }"
              >
                <i class="mdi mdi-code-tags" aria-hidden="true"></i>
              </button>
            }
          </div>
        </div>
      </div>
    }

    <!-- ── IMAGE, VIDEO, PDF ──────────────────────────── -->
    @if (isMediaFormat()) {
      <div class="row">
        <div class="col-lg-12">
          <div class="upload-area">
            @if (isUploading("standalone")) {
              <loading-component class="loading" diameter="20" />
            } @else if (!group.get("url")?.value) {
              <div class="placeholder-content" (click)="pickStandaloneFile()">
                <i class="mdi mdi-cloud-upload-outline upload-icon text-primary"></i>
                <p class="text-primary">
                  {{ "rcsMessage.editor.clickToUpload" | translate }}
                </p>
                <p class="text-muted">
                  @if (isFormat("image")) {
                    {{ "rcsMessage.editor.hintImage" | translate }}
                  } @else if (isFormat("video")) {
                    {{ "rcsMessage.editor.hintVideo" | translate }}
                  } @else if (isFormat("pdf")) {
                    {{ "rcsMessage.editor.hintDocument" | translate }}
                  }
                </p>
              </div>
            }
            <div class="upload-area__image">
              @if (group.get("url")?.value) {
                @if (isFormat("image")) {
                  <img
                    class="campaign-media-preview"
                    [src]="$safeNavigationMigration(group.get('url')?.value)"
                    [alt]="'rcsMessage.editor.fileAlt' | translate"
                    (click)="$event.stopPropagation()"
                    (error)="onMediaLoadError(group.get('url'))"
                  />
                  <button
                    class="btn btn-soft-danger"
                    type="button"
                    [disabled]="readonly"
                    (click)="removeStandaloneMedia(); $event.stopPropagation()"
                  >
                    <i class="mdi mdi-trash-can-outline"></i>
                  </button>
                } @else if (isFormat("video")) {
                  <video class="campaign-media-preview" controls (click)="$event.stopPropagation()">
                    <source [src]="$safeNavigationMigration(group.get('url')?.value)" type="video/mp4" />
                    {{ "rcsMessage.editor.videoNotSupported" | translate }}
                  </video>
                  <button
                    class="btn btn-soft-danger mt-2"
                    type="button"
                    [disabled]="readonly"
                    (click)="removeStandaloneMedia(); $event.stopPropagation()"
                  >
                    <i class="mdi mdi-trash-can-outline"></i>
                  </button>
                } @else if (isFormat("pdf")) {
                  <div class="d-flex flex-column align-items-center">
                    <a [href]="$safeNavigationMigration(group.get('url')?.value)" target="_blank" class="mb-2">
                      {{ "rcsMessage.editor.viewPdf" | translate }}
                    </a>
                    <button
                      class="btn btn-soft-danger"
                      type="button"
                      [disabled]="readonly"
                      (click)="removeStandaloneMedia(); $event.stopPropagation()"
                    >
                      <i class="mdi mdi-trash-can-outline"></i>
                    </button>
                  </div>
                }
              }
            </div>
            <input
              #standaloneFile
              type="file"
              [accept]="acceptFiles()"
              (change)="onStandaloneFile($event)"
            />
          </div>
          <field-error-component
            [field]="'url'"
            [label]="'rcsMessage.editor.fileUrlLabel' | translate"
            [formGroup]="group"
          />
        </div>
      </div>
    }

    <!-- ── RICH CARD ──────────────────────────── -->
    @if (isFormat("richCard")) {
      <ng-container formGroupName="richCard">
        <div class="row mb-3">
          <div class="col-12">
            <div class="d-flex justify-content-between align-items-center">
              <label class="form-label">
                {{ "rcsMessage.editor.cardTitle" | translate }}
                <span class="text-danger">*</span>
              </label>
              <ng-container
                [ngTemplateOutlet]="cardVariableButton"
                [ngTemplateOutletContext]="{ control: richCard.get('title') }"
              />
            </div>
            <input
              type="text"
              class="form-control"
              [maxlength]="CARD_TITLE_LIMIT"
              formControlName="title"
              [placeholder]="'rcsMessage.editor.cardTitlePlaceholder' | translate"
            />
            <small class="text-muted d-block text-end">
              {{ richCard.get("title")?.value?.length || 0 }}/{{ CARD_TITLE_LIMIT }}
            </small>
            <field-error-component
              [field]="'title'"
              [label]="'rcsMessage.editor.cardTitleLabel' | translate"
              [formGroup]="richCard"
            />
          </div>
        </div>

        <div class="row mb-3">
          <div class="col-12">
            <div class="d-flex justify-content-between align-items-center">
              <label class="form-label">
                {{ "rcsMessage.editor.cardDescription" | translate }}
                <span class="text-danger">*</span>
              </label>
              <ng-container
                [ngTemplateOutlet]="cardVariableButton"
                [ngTemplateOutletContext]="{ control: richCard.get('description') }"
              />
            </div>
            <textarea
              class="form-control"
              rows="3"
              [maxlength]="CARD_DESCRIPTION_LIMIT"
              formControlName="description"
              [placeholder]="'rcsMessage.editor.cardDescriptionPlaceholder' | translate"
            ></textarea>
            <small class="text-muted d-block text-end">
              {{ richCard.get("description")?.value?.length || 0 }}/{{ CARD_DESCRIPTION_LIMIT }}
            </small>
            <field-error-component
              [field]="'description'"
              [label]="'rcsMessage.editor.cardDescriptionLabel' | translate"
              [formGroup]="richCard"
            />
          </div>
        </div>

        <div class="row">
          <div class="col-lg-12">
            <div class="upload-area">
              @if (isUploading("richCard")) {
                <loading-component class="loading" diameter="20" />
              } @else if (!richCard.get("fileUrl")?.value) {
                <div class="placeholder-content" (click)="pickRichCardFile()">
                  <i class="mdi mdi-cloud-upload-outline upload-icon text-primary"></i>
                  <p class="text-primary">
                    {{ "rcsMessage.editor.clickToUpload" | translate }}
                  </p>
                  <p class="text-muted">
                    {{ "rcsMessage.editor.hintCardImage" | translate }}
                    <br />
                    {{ "rcsMessage.editor.hintCardVideo" | translate }}
                    <br />
                    {{ "rcsMessage.editor.hintDocument" | translate }}
                  </p>
                </div>
              }
              <div class="upload-area__image">
                @if (richCard.get("fileUrl")?.value) {
                  <ng-container
                    [ngTemplateOutlet]="cardMedia"
                    [ngTemplateOutletContext]="{ card: richCard }"
                  />
                  <button
                    class="btn btn-soft-danger"
                    type="button"
                    [disabled]="readonly"
                    (click)="removeRichCardMedia(); $event.stopPropagation()"
                  >
                    <i class="mdi mdi-trash-can-outline"></i>
                  </button>
                }
              </div>
              <input
                #richCardFile
                type="file"
                accept="image/jpeg, image/png, video/mp4, video/x-msvideo, application/pdf"
                (change)="onRichCardFile($event)"
              />
            </div>
            <field-error-component
              [field]="'fileUrl'"
              [label]="'rcsMessage.editor.fileUrlLabel' | translate"
              [formGroup]="richCard"
            />
          </div>
        </div>

        <div class="row">
          <div class="col-lg-12">
            <div class="variable" formArrayName="suggestions">
              @for (item of richCardSuggestions.controls; track item; let index = $index) {
                <ng-container
                  [ngTemplateOutlet]="suggestionFields"
                  [ngTemplateOutletContext]="{ suggestion: item, index: index, cardIndex: null }"
                />
              }
            </div>

            @if (richCardSuggestions.controls.length < MAX_SUGGESTIONS) {
              <button
                type="button"
                class="btn btn-primary"
                [disabled]="readonly"
                (click)="addRichCardSuggestion()"
              >
                {{ "rcsMessage.editor.addButton" | translate }}
              </button>
            }
          </div>
        </div>
      </ng-container>
    }

    <!-- ── CAROUSEL ──────────────────────────── -->
    @if (isFormat("carousel")) {
      <div class="row">
        <div class="col-lg-12 mb-3">
          <div class="carousel-cards" formArrayName="carousel">
            @for (card of carouselCards.controls; track card; let cardIndex = $index) {
              <div [formGroupName]="cardIndex" class="campaign-panel mb-3">
                <div class="d-flex justify-content-between align-items-center mb-3">
                  <span class="campaign-group-label mb-0">
                    {{ "rcsMessage.editor.cardN" | translate: { n: cardIndex + 1 } }}
                  </span>
                  <button
                    type="button"
                    class="btn btn-soft-danger"
                    [disabled]="readonly"
                    [attr.aria-label]="'rcsMessage.editor.removeCard' | translate"
                    (click)="removeCarouselCard(cardIndex)"
                  >
                    <i class="mdi mdi-trash-can-outline"></i>
                  </button>
                </div>

                <div>
                  <div class="row mb-3">
                    <div class="col-12">
                      <div class="d-flex justify-content-between align-items-center">
                        <label class="form-label">
                          {{ "rcsMessage.editor.cardTitle" | translate }}
                          <span class="text-danger">*</span>
                        </label>
                        <ng-container
                          [ngTemplateOutlet]="cardVariableButton"
                          [ngTemplateOutletContext]="{ control: card.get('title') }"
                        />
                      </div>
                      <input
                        type="text"
                        class="form-control"
                        [maxlength]="CARD_TITLE_LIMIT"
                        formControlName="title"
                        [placeholder]="'rcsMessage.editor.cardTitlePlaceholder' | translate"
                      />
                      <small class="text-muted d-block text-end">
                        {{ card.get("title")?.value?.length || 0 }}/{{ CARD_TITLE_LIMIT }}
                      </small>
                      <field-error-component
                        [field]="'title'"
                        [label]="'rcsMessage.editor.cardTitleLabel' | translate"
                        [formGroup]="$any(card)"
                      />
                    </div>
                  </div>

                  <div class="row mb-3">
                    <div class="col-12">
                      <div class="d-flex justify-content-between align-items-center">
                        <label class="form-label">
                          {{ "rcsMessage.editor.cardDescription" | translate }}
                          <span class="text-danger">*</span>
                        </label>
                        <ng-container
                          [ngTemplateOutlet]="cardVariableButton"
                          [ngTemplateOutletContext]="{ control: card.get('description') }"
                        />
                      </div>
                      <textarea
                        class="form-control"
                        rows="3"
                        [maxlength]="CARD_DESCRIPTION_LIMIT"
                        formControlName="description"
                        [placeholder]="'rcsMessage.editor.cardDescriptionPlaceholder' | translate"
                      ></textarea>
                      <small class="text-muted d-block text-end">
                        {{ card.get("description")?.value?.length || 0 }}/{{ CARD_DESCRIPTION_LIMIT }}
                      </small>
                      <field-error-component
                        [field]="'description'"
                        [label]="'rcsMessage.editor.cardDescriptionLabel' | translate"
                        [formGroup]="$any(card)"
                      />
                    </div>
                  </div>

                  <div class="upload-area mb-3">
                    @if (isUploading(cardIndex)) {
                      <loading-component class="loading" diameter="20" />
                    } @else if (card.get("fileUrl")?.value) {
                      <div class="upload-area__image">
                        <ng-container
                          [ngTemplateOutlet]="cardMedia"
                          [ngTemplateOutletContext]="{ card: card }"
                        />
                        <button
                          class="btn btn-soft-danger"
                          type="button"
                          [disabled]="readonly"
                          (click)="removeCarouselMedia(cardIndex); $event.stopPropagation()"
                        >
                          <i class="mdi mdi-trash-can-outline"></i>
                        </button>
                      </div>
                    } @else {
                      <div class="placeholder-content" (click)="pickCarouselFile(cardIndex)">
                        <i class="mdi mdi-cloud-upload-outline upload-icon text-primary"></i>
                        <p class="text-primary">
                          {{ "rcsMessage.editor.clickToUpload" | translate }}
                        </p>
                        <p class="text-muted">
                          {{ "rcsMessage.editor.hintCardImage" | translate }}
                          <br />
                          {{ "rcsMessage.editor.hintCardVideo" | translate }}
                          <br />
                          {{ "rcsMessage.editor.hintDocument" | translate }}
                        </p>
                      </div>
                    }
                  </div>

                  <div class="suggestions-section">
                    <div formArrayName="suggestions">
                      @for (suggestion of suggestionsOf(card).controls; track suggestion; let suggestionIndex = $index) {
                        <ng-container
                          [ngTemplateOutlet]="suggestionFields"
                          [ngTemplateOutletContext]="{ suggestion: suggestion, index: suggestionIndex, cardIndex: cardIndex }"
                        />
                      }
                    </div>

                    <div class="d-flex align-items-center mb-2">
                      @if (suggestionsOf(card).length < MAX_SUGGESTIONS) {
                        <button
                          type="button"
                          class="btn btn-sm btn-primary"
                          [disabled]="readonly"
                          (click)="addCarouselSuggestion(cardIndex)"
                        >
                          <i class="mdi mdi-plus me-1"></i>
                          {{ "rcsMessage.editor.addButton" | translate }}
                        </button>
                      }
                    </div>
                  </div>
                </div>
              </div>
            }
          </div>
          <!-- One file input for every card: the card it fills is the one whose area was clicked. -->
          <input
            #carouselFile
            type="file"
            class="d-none"
            accept="image/jpeg, image/png, video/mp4, video/x-msvideo, application/pdf"
            (change)="onCarouselFile($event)"
          />
          <field-error-component
            [field]="'carousel'"
            [label]="'rcsMessage.editor.carouselLabel' | translate"
            [formGroup]="group"
          />

          <div class="d-flex align-items-center mb-3">
            @if (carouselCards.controls.length < MAX_CAROUSEL_CARDS) {
              <button
                type="button"
                class="btn btn-sm btn-primary"
                [disabled]="readonly"
                (click)="addCarouselCard()"
              >
                <i class="mdi mdi-plus me-1"></i>
                {{ "rcsMessage.editor.addCard" | translate }}
              </button>
            }
          </div>
        </div>
      </div>
    }
  </div>
</div>

<!-- The media of a rich card or a carousel card, by its content type. -->
<ng-template #cardMedia let-card="card">
  @if (card.get("fileContentType")?.value === "image/jpeg" || card.get("fileContentType")?.value === "image/png") {
    <img
      class="campaign-media-preview"
      [src]="$safeNavigationMigration(card.get('fileUrl')?.value)"
      [alt]="'rcsMessage.editor.imageAlt' | translate"
      (click)="$event.stopPropagation()"
      (error)="onMediaLoadError(card.get('fileUrl'))"
    />
  } @else if (card.get("fileContentType")?.value === "video/mp4" || card.get("fileContentType")?.value === "video/x-msvideo") {
    <video class="campaign-media-preview" controls (click)="$event.stopPropagation()">
      <source [src]="$safeNavigationMigration(card.get('fileUrl')?.value)" type="video/mp4" />
      {{ "rcsMessage.editor.videoNotSupported" | translate }}
    </video>
  } @else if (card.get("fileContentType")?.value === "application/pdf") {
    <div class="d-flex flex-column align-items-center">
      <a [href]="$safeNavigationMigration(card.get('fileUrl')?.value)" target="_blank" class="mb-2">
        {{ "rcsMessage.editor.viewPdf" | translate }}
      </a>
    </div>
  }
</ng-template>

<!-- One suggestion (button) of a rich card or a carousel card. -->
<ng-template #suggestionFields let-suggestion="suggestion" let-index="index" let-cardIndex="cardIndex">
  <div [formGroup]="$any(suggestion)" class="row">
    <div class="col-lg-3 mb-3">
      <label class="form-label">
        {{ "rcsMessage.editor.buttonType" | translate }}
        <span class="text-danger">*</span>
      </label>
      <mat-select
        class="form-control"
        [placeholder]="'rcsMessage.editor.selectButtonTypePlaceholder' | translate"
        formControlName="type"
      >
        <mat-option [value]="'openUrl'">URL</mat-option>
        <mat-option [value]="'call'">{{ "rcsMessage.editor.optionCall" | translate }}</mat-option>
        <mat-option [value]="'reply'">{{ "rcsMessage.editor.optionText" | translate }}</mat-option>
      </mat-select>
      <field-error-component
        [field]="'type'"
        [label]="'rcsMessage.editor.buttonTypeLabel' | translate"
        [formGroup]="$any(suggestion)"
      />
    </div>
    <div class="col-lg-4">
      <div class="d-flex justify-content-between align-items-center">
        <label class="form-label">
          {{ "rcsMessage.editor.buttonTitle" | translate }}
          <span class="text-danger">*</span>
        </label>
        <ng-container
          [ngTemplateOutlet]="cardVariableButton"
          [ngTemplateOutletContext]="{ control: suggestion.get('title') }"
        />
      </div>
      <input type="text" class="form-control" [maxlength]="SUGGESTION_LIMIT" formControlName="title" />
      <field-error-component
        [field]="'title'"
        [label]="'rcsMessage.editor.buttonTitleLabel' | translate"
        [formGroup]="$any(suggestion)"
      />
    </div>
    <div class="col-lg-5 position-relative">
      <div class="d-flex justify-content-between align-items-center">
        <label class="form-label">
          {{ "rcsMessage.editor.buttonValue" | translate }}
          <span class="text-danger">*</span>
        </label>
        <ng-container
          [ngTemplateOutlet]="cardVariableButton"
          [ngTemplateOutletContext]="{ control: suggestion.get('value') }"
        />
      </div>
      <div class="d-flex align-items-center">
        <input
          type="text"
          class="form-control"
          [maxlength]="SUGGESTION_LIMIT"
          formControlName="value"
          [placeholder]="suggestionPlaceholder(suggestion)"
        />
        <button
          type="button"
          class="btn btn-link p-0 ms-2"
          [disabled]="readonly"
          [attr.aria-label]="'rcsMessage.editor.removeButton' | translate"
          (click)="removeSuggestion(cardIndex, index)"
        >
          <i class="mdi mdi-trash-can-outline fs-4 text-danger"></i>
        </button>
      </div>
      <field-error-component
        [field]="'value'"
        [label]="'rcsMessage.editor.buttonValueLabel' | translate"
        [formGroup]="$any(suggestion)"
      />
    </div>
  </div>
</ng-template>

<!-- The variable button of a card or suggestion field — shown only where the host enables it. -->
<ng-template #cardVariableButton let-control="control">
  @if (cardVariables && variables.length) {
    <button
      type="button"
      class="btn btn-link p-0"
      [disabled]="readonly"
      [attr.aria-label]="'rcsMessage.editor.insertVariable' | translate"
      [matMenuTriggerFor]="variableMenu"
      [matMenuTriggerData]="{ control: control }"
    >
      <i class="mdi mdi-code-tags" aria-hidden="true"></i>
    </button>
  }
</ng-template>

<mat-menu #variableMenu="matMenu">
  <ng-template matMenuContent let-control="control">
    @for (option of variables; track option.token) {
      <button mat-menu-item type="button" (click)="insertVariable(control, option.token)">
        <i class="mdi {{ option.icon ?? 'mdi-code-braces' }}" aria-hidden="true"></i>
        {{ variableLabel(option) }}
      </button>
    }
  </ng-template>
</mat-menu>
